import React, { useState, useRef, useEffect } from "react";
import { Modal } from "antd";
import { AttachmentItem } from "./types";

interface AttachmentsAndRemarksProps {
    userData: any;
    remark: string;
    setRemark: (remark: string) => void;
    attachments: AttachmentItem[];
    setAttachments: (attachments: AttachmentItem[]) => void;
    fileUploadError?: string;
    allowAttachment?: boolean;
    readOnly?: boolean;
}

export const AttachmentsAndRemarksSection: React.FC<AttachmentsAndRemarksProps> = ({
    userData,
    remark,
    setRemark,
    attachments,
    setAttachments,
    fileUploadError,
    allowAttachment = true,
    readOnly = false,
}) => {
    const [showRemarkModal, setShowRemarkModal] = useState(false);
    const [tempRemark, setTempRemark] = useState("");
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Reset native file input if attachments are cleared
    useEffect(() => {
        if (attachments.length === 0 && fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [attachments]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const newAttachments: AttachmentItem[] = files.map((file, index) => ({
            id: Date.now() + index,
            file,
            name: file.name,
            size: file.size,
        }));
        setAttachments([...attachments, ...newAttachments]);
        if (e.target) {
            e.target.value = "";
        }
    };

    const handleRemoveFile = (id: number) => {
        setAttachments(attachments.filter((a) => a.id !== id));
    };

    const handleOpenFilePicker = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const now = new Date();
    const currentDate = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;
    const currentTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const employeeName = userData?.details?.firstname
        ? `${userData.details.firstname} ${userData.details.lastname || ""}`.trim()
        : userData?.empName || "Employee";

    return (
        <>
            {/* Comments Card */}
            <div className="card border-0 shadow-sm rounded-3 mb-3">
                <div className="card-header bg-white border-0 py-3 px-3 d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-bold fs-14" style={{ color: "#A93226" }}>
                        Comments
                    </h6>
                    {!readOnly && (
                        <button
                            type="button"
                            className="btn btn-sm text-white px-3 py-1 rounded-2 fs-12 fw-medium"
                            style={{ backgroundColor: "#A93226" }}
                            onClick={() => {
                                setTempRemark(remark);
                                setShowRemarkModal(true);
                            }}
                        >
                            + Add Remark
                        </button>
                    )}
                </div>
                <div className="card-body pt-0 px-3 pb-3">
                    <div className="row g-2 fs-12 text-dark">
                        <div className="col-12 d-flex justify-content-between border-bottom pb-1">
                            <span className="text-muted">User Type:</span>
                            <span className="fw-medium">Employee</span>
                        </div>
                        <div className="col-12 d-flex justify-content-between border-bottom pb-1">
                            <span className="text-muted">User Name:</span>
                            <span className="fw-medium text-uppercase">{employeeName}</span>
                        </div>
                        <div className="col-12 d-flex justify-content-between border-bottom pb-1">
                            <span className="text-muted">Date:</span>
                            <span className="fw-medium">{currentDate}</span>
                        </div>
                        <div className="col-12 d-flex justify-content-between border-bottom pb-1">
                            <span className="text-muted">Time:</span>
                            <span className="fw-medium">{currentTime}</span>
                        </div>
                        <div className="col-12 d-flex justify-content-between border-bottom pb-1">
                            <span className="text-muted">Previous Status:</span>
                            <span className="fw-medium">-</span>
                        </div>
                        <div className="col-12 d-flex justify-content-between border-bottom pb-1">
                            <span className="text-muted">Current Status:</span>
                            <span className="fw-medium">-</span>
                        </div>
                        <div className="col-12 pt-1">
                            <span className="text-muted d-block mb-1">Remarks:</span>
                            <div className="p-2 bg-light rounded-2 text-secondary fs-12">
                                {remark ? remark : "No remarks provided"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Attachments Card */}
            {allowAttachment && (
                <div className="card border-0 shadow-sm rounded-3 mb-3">
                    <div className="card-header bg-white border-0 py-3 px-3">
                        <h6 className="mb-0 fw-bold fs-14" style={{ color: "#A93226" }}>
                            Attachments (Max size 2 MB)
                        </h6>
                        <small className="text-muted fs-12">{readOnly ? "Uploaded Documents" : "Upload Documents"}</small>
                    </div>
                    <div className="card-body pt-0 px-3 pb-3">
                        {!readOnly && (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    id="reimbursementFileInput"
                                    className="d-none"
                                    multiple
                                    onChange={handleFileChange}
                                />
                                <div className="border rounded-2 p-1 d-flex align-items-center justify-content-between bg-white">
                                    <span
                                        className="text-muted ps-2 fs-13 cursor-pointer text-truncate"
                                        onClick={handleOpenFilePicker}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <i className="ti ti-paperclip me-2"></i>
                                        Choose File
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm text-white px-4 py-1.5 rounded-2 fs-12"
                                        style={{ backgroundColor: "#A93226" }}
                                        onClick={handleOpenFilePicker}
                                    >
                                        Upload
                                    </button>
                                </div>

                                {fileUploadError && <div className="text-danger small mt-2">{fileUploadError}</div>}
                            </>
                        )}

                        {attachments.length > 0 ? (
                            <div className="mt-3 row g-2">
                                {attachments.map((att) => (
                                    <div key={att.id} className="col-md-6 col-lg-4">
                                        <div className="p-2 border rounded-3 d-flex align-items-center justify-content-between bg-white shadow-xs">
                                            <div className="d-flex align-items-center overflow-hidden">
                                                <i className="ti ti-file-text fs-20 text-primary me-2"></i>
                                                <div className="text-truncate">
                                                    <div className="text-dark fw-medium text-truncate small">{att.name}</div>
                                                    <small className="text-muted">{(att.size / 1024).toFixed(1)} KB</small>
                                                </div>
                                            </div>
                                            {!readOnly && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm text-danger p-1"
                                                    onClick={() => handleRemoveFile(att.id)}
                                                >
                                                    <i className="ti ti-trash"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            readOnly && (
                                <div className="text-muted small py-2">No attachments uploaded</div>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* Modal for adding remarks */}
            <Modal
                title="Add Remark"
                open={showRemarkModal}
                onOk={() => {
                    setRemark(tempRemark);
                    setShowRemarkModal(false);
                }}
                onCancel={() => setShowRemarkModal(false)}
                okText="Save Remark"
                okButtonProps={{ style: { backgroundColor: "#A93226", borderColor: "#A93226" } }}
                centered
            >
                <textarea
                    className="form-control mt-2"
                    rows={4}
                    placeholder="Enter your remarks here..."
                    value={tempRemark}
                    onChange={(e) => setTempRemark(e.target.value)}
                />
            </Modal>
        </>
    );
};
